/*
 * What one tab is allowed to hold. The DOM has had a hard cap since the list was written
 * (`list/WindowedList.tsx`, 300 rows); memory had none, and a window grew for as long as a tab was
 * open — by scrolling back fifty rows at a time and by every message that arrived. At 826 bytes of
 * model and render tree per row, fifty conversations of a thousand rows are 41 MB that a tab never
 * gives back, and every arrival costs one copy of the whole array plus one `buildRows` pass over it.
 *
 * Two rules bound it, and both are about *what nobody is looking at*.
 *
 * **A window is capped at `MAX_WINDOW_ROWS` rows** and trimmed from the end the reader is moving
 * away from: a message arriving at the tail drops the oldest row, a page of history above the window
 * drops the newest. What is dropped is not hidden — `hasMoreBefore` and `hasMoreAfter` say so, which
 * is what keeps "Load earlier", the jump pill and `loadNewer` honest — and the server is where those
 * rows come back from, which is where they came from in the first place. The DOM holds 300 rows, so
 * a thousand is three windows of runway either way before a page is fetched.
 *
 * **A window nobody has visited for `EVICT_AFTER_MS` is given back.** The conversation keeps its
 * sidebar row, its counter and its unread count — those are not the window — and goes back to
 * `cold`, which is exactly the state it had before it was first opened and what makes the surface
 * fetch a fresh page when the reader returns. Which conversation and which thread a surface has on
 * screen right now is the one thing this file remembers between calls, because it is a fact about
 * *this visit* and not about the model: the model is persisted, and a timestamp of when a pane was
 * last mounted has no business on anybody's disk (front-6 is the same rule applied to the slice).
 */

import type { ConversationId, Model } from "../../../core/store/model.ts"
import { chatSlice, withChatSlice } from "./selectors.ts"
import type { ChatSlice } from "./slice.ts"

/**
 * How many rows of one conversation stay in memory (Q14). Four DOM windows: a reader who wheels
 * upwards has 700 rows of runway above the 300 on screen before the far end is trimmed at all, and
 * the trim itself is invisible to them because it happens at the end they are moving away from.
 */
export const MAX_WINDOW_ROWS = 1000

/** How many replies of one thread stay in memory; a pane is one column, so the same ceiling. */
export const MAX_THREAD_ROWS = MAX_WINDOW_ROWS

/** How long a window outlives the visit that loaded it (Q14). */
export const EVICT_AFTER_MS = 30 * 60 * 1000

/** Which end of a window the reader is moving away from, i.e. which end may be dropped. */
export type DropEnd = "oldest" | "newest"

/** What is on either side of a capped run: the flags the surface draws its two doors from. */
export interface Bounds {
	hasMoreBefore: boolean
	hasMoreAfter: boolean
}

export interface CappedRows<T> extends Bounds {
	rows: T[]
}

/**
 * `rows` cut down to `ceiling`, dropping from `drop` and telling the truth about what went. The rows
 * array is handed back unchanged when it already fits, so a window that is inside the ceiling — every
 * window, almost always — costs this nothing but a length comparison.
 *
 * Both flags are raised rather than replaced: a run that already declared history above it still
 * does after its tail was cut.
 */
export function capRows<T extends { seq: number }>(
	rows: T[],
	bounds: Bounds,
	drop: DropEnd,
	ceiling = MAX_WINDOW_ROWS,
): CappedRows<T> {
	if (rows.length <= ceiling) {
		return { rows, hasMoreBefore: bounds.hasMoreBefore, hasMoreAfter: bounds.hasMoreAfter }
	}
	return drop === "oldest"
		? { rows: rows.slice(-ceiling), hasMoreBefore: true, hasMoreAfter: bounds.hasMoreAfter }
		: { rows: rows.slice(0, ceiling), hasMoreBefore: bounds.hasMoreBefore, hasMoreAfter: true }
}

/**
 * The same for a thread's replies, which have only one door: a thread window is always anchored at
 * its newest reply (`slice.ts`), so there is no flag that could say "newer replies are unloaded" and
 * the only end that may be dropped is the oldest. That is the end an arriving reply pushes against;
 * a reader paging *back* through more than the ceiling keeps what they asked for.
 */
export function capReplies<T extends { seq: number }>(
	rows: T[],
	hasMoreBefore: boolean,
	ceiling = MAX_THREAD_ROWS,
): { rows: T[]; hasMoreBefore: boolean } {
	if (rows.length <= ceiling) return { rows, hasMoreBefore }
	return { rows: rows.slice(-ceiling), hasMoreBefore: true }
}

// ---- which windows are being looked at ----

/**
 * The conversations and threads a surface has on screen, by key, with how many surfaces hold each —
 * the timeline and a thread pane of the same conversation are two, and a window is held while either
 * stands. Beside it, when each was last let go.
 *
 * Module state and not model state on purpose: this is what *this tab* is showing at this moment. It
 * is the same shape as the module-level caches this codebase already keeps beside the model — the
 * sidebar's order memo (`state/selectors.ts`), the quoted-message cache (`render/QuotedMessage.tsx`).
 */
const held = new Map<string, number>()
const leftAt = new Map<string, number>()

function conversationKey(conv: ConversationId): string {
	return `c${String(conv)}`
}

function threadKey(rootId: number): string {
	return `t${String(rootId)}`
}

function hold(key: string): () => void {
	held.set(key, (held.get(key) ?? 0) + 1)
	leftAt.delete(key)
	let released = false
	return () => {
		// A surface may be unmounted twice in React's development double-invoke, and the effect that
		// takes the hold runs again after it; one release per hold is what keeps the count honest.
		if (released) return
		released = true
		const count = (held.get(key) ?? 1) - 1
		if (count > 0) {
			held.set(key, count)
			return
		}
		held.delete(key)
		leftAt.set(key, Date.now())
	}
}

/** Marks a conversation's window as being on screen; the answer ends the visit. */
export function holdConversation(conv: ConversationId): () => void {
	return hold(conversationKey(conv))
}

/** The same for one thread's replies, which a pane holds for as long as it is open. */
export function holdThread(rootId: number): () => void {
	return hold(threadKey(rootId))
}

/** Forgets every visit. For tests, which share a module with the tab they simulate. */
export function forgetVisits(): void {
	held.clear()
	leftAt.clear()
}

/**
 * Whether what this key names may be given back now: nothing is holding it, and it was let go longer
 * ago than the idle time. A key nothing has ever held is *not* evictable, because it has no beginning
 * to measure half an hour from — `noteLoadedWindows` gives it one.
 */
function idle(key: string, now: number, idleMs: number): boolean {
	if (held.has(key)) return false
	const left = leftAt.get(key)
	return left !== undefined && now - left >= idleMs
}

/**
 * Records every loaded window this tab has never had on screen as one that was let go just now, which
 * is what gives it the same half hour as a visited one.
 *
 * A window arrives without a surface more often than the rule above would suggest, and the warm boot
 * is the case that matters: `core/persist` puts a window back for every conversation it has rows for
 * — up to 200 rows each, dozens of them — and no pane has held a single one. Taken for windows whose
 * surface is long gone they were all given back by the first sweep of the visit, however recently the
 * boot had read them, and with them (the model being what the disk is written from) the stored rows
 * the next boot would have rendered from. The store's own gap refetch is the other case: it fetches a
 * conversation's tail whether or not anybody is looking at it.
 *
 * It is the sweeper's first step rather than the boot's because the boot is core's and may not call
 * into a module (architecture.md D21) — and because this covers every way a window can appear rather
 * than the one the boot knows about.
 */
export function noteLoadedWindows(model: Model, now: number): void {
	for (const key of loadedKeys(model)) note(key, now)
}

function note(key: string, now: number): void {
	if (held.has(key) || leftAt.has(key)) return
	leftAt.set(key, now)
}

/** The key of every window the model holds: one per loaded conversation window, one per thread. */
function loadedKeys(model: Model): Set<string> {
	const keys = new Set<string>()
	for (const key of Object.keys(model.messages)) keys.add(conversationKey(Number(key)))
	for (const key of Object.keys(chatSlice(model).threads)) keys.add(threadKey(Number(key)))
	return keys
}

/**
 * How long until the next window may be given back, or `undefined` when none is waiting — every
 * window is either on screen or has been given back already.
 */
export function nextEvictionDue(now: number, idleMs = EVICT_AFTER_MS): number | undefined {
	let earliest: number | undefined
	for (const left of leftAt.values()) {
		const due = Math.max(0, left + idleMs - now)
		if (earliest === undefined || due < earliest) earliest = due
	}
	return earliest
}

/**
 * What the sweeper calls once it has swept, with the model its sweep left behind. Two kinds of
 * expired visit come out of a sweep, and they must not be treated alike.
 *
 * A visit whose window is **gone** is forgotten. Left standing it would be due for ever:
 * `nextEvictionDue` answers 0 the moment the idle time has passed, and a sweeper that arms itself
 * from that answer would arm itself again immediately, over and over, for a window it has already
 * given back.
 *
 * A visit whose window is **still there** was refused by `evictIdle` — a page of it is in flight, and
 * a window the fetch is about to land in is not given back. It is dated to now instead, which gives
 * it the idle time again: the same beginning `noteLoadedWindows` gives a window that arrived without
 * a surface, and the sweeper arms its timer for it. Forgetting it here would leave a window nobody is
 * looking at in memory with nothing due and no timer armed, to be given back only when some other
 * visit happened to end; leaving it due unchanged would be the other mistake, the sweeper arming
 * itself for "now" over and over for as long as the fetch is out.
 */
export function forgetSweptVisits(model: Model, now: number, idleMs = EVICT_AFTER_MS): void {
	const loaded = loadedKeys(model)
	for (const [key, left] of leftAt) {
		if (now - left < idleMs) continue
		if (loaded.has(key)) leftAt.set(key, now)
		else leftAt.delete(key)
	}
}

/**
 * The model with every window and thread nobody has looked at for `idleMs` given back. The
 * conversation's row stays — its counter, its unread count and its place in the sidebar are not the
 * window — and its status goes back to `cold`, which is what makes the surface ask for a fresh page
 * when the reader returns (`ConversationView`). A conversation whose page is on its way is never
 * touched: the fetch would land in a window that is no longer there.
 *
 * Identity is kept for everything that stays, so a sweep that finds nothing to give back returns the
 * very same model and costs no render.
 */
export function evictIdle(model: Model, now: number, idleMs = EVICT_AFTER_MS): Model {
	let messages: Model["messages"] | undefined
	let conversations: Model["conversations"] | undefined
	for (const key of Object.keys(model.messages)) {
		const conv = Number(key)
		const slice = model.conversations[conv]
		if (slice?.status === "loading" || slice?.status === "resyncPending") continue
		if (!idle(conversationKey(conv), now, idleMs)) continue
		messages ??= { ...model.messages }
		delete messages[conv]
		if (slice !== undefined && slice.status !== "cold") {
			conversations ??= { ...model.conversations }
			conversations[conv] = { ...slice, status: "cold" }
		}
	}

	const slice = chatSlice(model)
	let threads: ChatSlice["threads"] | undefined
	for (const key of Object.keys(slice.threads)) {
		const rootId = Number(key)
		const window = slice.threads[rootId]
		if (window?.status === "loading") continue
		if (!idle(threadKey(rootId), now, idleMs)) continue
		threads ??= { ...slice.threads }
		delete threads[rootId]
	}

	if (messages === undefined && conversations === undefined && threads === undefined) return model
	const next: Model = { ...model }
	if (messages !== undefined) next.messages = messages
	if (conversations !== undefined) next.conversations = conversations
	return threads === undefined ? next : withChatSlice(next, { ...slice, threads })
}
